import {
  FREE_FOR_ALL,
  IKEA_BUSINESS_NETWORK_ONLY,
  IKEA_FAMILY_ONLY,
  userAuthStates,
} from '../../enums';
import { EventLoyaltyType, LoyaltyPrograms, User } from '../../types/model';

const AUTH_TOKEN = 'AUTH_TOKEN';

export function getAuthToken() {
  const authHeader = sessionStorage.getItem(AUTH_TOKEN);
  return authHeader || '';
}

export async function getAuthTokenMod() {
  const loggedInStatus = await window?.ikea?.authentication?.isLoggedIn();
  return loggedInStatus.isLoggedIn ? loggedInStatus.authToken : '';
}

export const setAuthToken = (setSessionToken?: Function): void => {
  const isBrowser = typeof window !== 'undefined';
  if (isBrowser) {
    if (
      window.ikea &&
      window.ikea.authentication &&
      window.ikea.authentication.getTokens
    ) {
      window.ikea.authentication.getTokens(
        (error: string | undefined, tokens: any) => {
          const token = tokens && tokens.auth && tokens.auth.value;
          if (!error && token) {
            sessionStorage.setItem(AUTH_TOKEN, token || '');
            if (setSessionToken) {
              setSessionToken(token);
            }
          } else {
            sessionStorage.removeItem(AUTH_TOKEN);
            if (setSessionToken) {
              setSessionToken('unknown');
            }
          }
        }
      );
    }
  }
};

export function getInfoFromJwt(jwt: string) {
  const [, payload] = jwt.split('.');

  const jsonPayload: {
    'https://accounts.ikea.com/retailUnit': string;
    'https://accounts.ikea.com/memberId': string;
    'https://accounts.ikea.com/partyUId': string;
    'https://accounts.ikea.com/customerType': string;
    'https://accounts.ikea.com/loyaltyPrograms': string[];
  } = JSON.parse(atob(payload));

  return {
    retailUnit: jsonPayload['https://accounts.ikea.com/retailUnit'],
    memberId: jsonPayload['https://accounts.ikea.com/memberId'],
    partyUId: jsonPayload['https://accounts.ikea.com/partyUId'],
    customerType: jsonPayload['https://accounts.ikea.com/customerType'],
    loyaltyPrograms: jsonPayload['https://accounts.ikea.com/loyaltyPrograms'],
  };
}

type UserDataType = {
  customers: {
    partyUId: string;
    firstName: string;
    lastName: string;
    contactMethods: {
      value: string;
      type: string;
    }[];
    loyaltyMemberships: {
      programCode: string;
      status: string;
    }[];
  }[];
};

export type FormatDataType = {
  countryCode: null | number;
  emailAddress: string;
  familyNumber: null | string;
  firstName: string;
  lastName: string;
  languageCode: string;
  partyUid: string;
  phoneNumber: null | string;
  companyName?: string;
  loyaltyPrograms: LoyaltyPrograms[] | [];
};

export function formatData(data: FormatDataType) {
  const {
    firstName,
    lastName,
    emailAddress,
    phoneNumber,
    loyaltyPrograms,
    companyName,
    ...rest
  } = data;
  const ret = {
    ...rest,
    format(): User {
      return {
        firstName,
        lastName,
        emailAddress,
        phoneNumber,
        partyUId: rest.partyUid,
        loyalty: rest.familyNumber ? rest.familyNumber : null,
        loyaltyPrograms,
        companyName,
        contact: {
          EMAIL: emailAddress,
          SMS: phoneNumber as string,
        },
      };
    },
  };
  return ret;
}

export function formatUserData(userData: UserDataType) {
  const [customer] = userData.customers;
  const loyalty = customer.loyaltyMemberships.map(
    (program) => program.programCode
  );
  const { partyUId, firstName, lastName, contactMethods } = customer;

  const contact = contactMethods.reduce((acc, item) => {
    if (!['EMAIL', 'SMS'].includes(item.type)) {
      return acc;
    }

    return { ...acc, [item.type]: item.value };
  }, {});

  return { loyalty, firstName, lastName, contact, partyUId };
}

export function getUserLoyaltyState(
  loyaltyPrograms: LoyaltyPrograms[] | null
): userAuthStates {
  return (
    (loyaltyPrograms?.includes(LoyaltyPrograms.ikeaFamily) &&
      userAuthStates.LOGGED_IN_AS_FAMILY) ||
    (loyaltyPrograms?.includes(LoyaltyPrograms.ikeaBusinessNetwork) &&
      userAuthStates.LOGGED_IN_AS_BUSINESS_NETWORK) ||
    userAuthStates.LOGGED_IN_AS_REGULAR
  );
}

export function isUserEligibleToRegisterToEvent(
  user: User,
  loyaltyEventType: EventLoyaltyType | undefined
): boolean {
  // return false if either param are null
  if (!user || !loyaltyEventType) return false;

  const userAuthState = getUserLoyaltyState(user?.loyaltyPrograms);
  return (
    (loyaltyEventType === IKEA_FAMILY_ONLY &&
      userAuthState === userAuthStates.LOGGED_IN_AS_FAMILY) ||
    (loyaltyEventType === IKEA_BUSINESS_NETWORK_ONLY &&
      userAuthState === userAuthStates.LOGGED_IN_AS_BUSINESS_NETWORK) ||
    ((loyaltyEventType === FREE_FOR_ALL ||
      loyaltyEventType === undefined) &&
      [
        userAuthStates.LOGGED_IN_AS_FAMILY,
        userAuthStates.LOGGED_IN_AS_BUSINESS_NETWORK,
        userAuthStates.LOGGED_IN_AS_REGULAR,
      ].includes(userAuthState))
  );
}
